<template>
  <div class="vendas-kpi-page">
    <PageHeader :title="t('Dashboard de Vendas')" />

    <!-- Loading State -->
    <template v-if="isLoading && !cards">
      <div class="kpi-grid kpi-grid--3">
        <DashboardSkeletonBlock height="140px" radius="16px" testid="vendas-kpi-skeleton" v-for="n in 3" :key="'hero-'+n" />
      </div>
      <div class="kpi-grid kpi-grid--5 loading-spacer">
        <DashboardSkeletonBlock height="120px" radius="16px" v-for="n in 5" :key="'sec-'+n" />
      </div>
      <div class="charts-row loading-spacer">
        <DashboardSkeletonBlock height="350px" radius="16px" />
        <DashboardSkeletonBlock height="350px" radius="16px" />
      </div>
      <div class="loading-spacer">
        <DashboardSkeletonBlock height="350px" radius="16px" />
      </div>
    </template>

    <!-- Error State -->
    <div v-else-if="error && !cards" class="empty-state">
      <i class="pi pi-exclamation-triangle empty-icon"></i>
      <p>{{ t('Erro ao carregar dados de vendas') }}</p>
      <Button :label="t('Tentar novamente')" @click="fetchAll" data-testid="vendas-kpi-retry" />
    </div>

    <!-- Empty State -->
    <div v-else-if="cards && isEmptyData() && !isLoading" class="empty-state">
      <i class="pi pi-info-circle empty-icon"></i>
      <p>{{ t('Nenhum dado de vendas encontrado para o período selecionado') }}</p>
    </div>

    <!-- Dashboard Content -->
    <template v-else-if="cards">
      <!-- Faixa 1: Hero Cards -->
      <div class="kpi-grid kpi-grid--3">
        <div class="hero-card variant-primary">
          <div class="hero-indicator"></div>
          <div class="hero-content">
            <div class="hero-value">{{ n(cards.meta_faturamento / 100, 'currency') }}</div>
            <div class="hero-label">{{ t('Meta de vendas mensal') }}</div>
            <div class="hero-subinfo">
              <span class="hero-percent" :class="percentClass(cards.percentual_atingimento)">
                {{ formatPercent(cards.percentual_atingimento) }}
              </span>
              <span class="hero-subtext">{{ t('atingido') }}</span>
            </div>
          </div>
        </div>

        <div class="hero-card variant-info">
          <div class="hero-indicator"></div>
          <div class="hero-content">
            <div class="hero-value">{{ n(cards.previsao_vendas / 100, 'currency') }}</div>
            <div class="hero-label">{{ t('Previsão de vendas') }}</div>
            <div class="hero-subinfo">
              <span class="hero-subvalue">{{ formatMoney(cards.ticket_medio_item) }}</span>
              <span class="hero-subtext">{{ t('valor médio por item') }}</span>
            </div>
          </div>
        </div>

        <div class="hero-card variant-success">
          <div class="hero-indicator"></div>
          <div class="hero-content">
            <div class="hero-value">{{ n(cards.vendas_realizadas / 100, 'currency') }}</div>
            <div class="hero-label">{{ t('Vendas realizadas neste mês') }}</div>
            <div class="hero-subinfo">
              <span class="hero-subvalue">{{ cards.clientes_atendidos }}</span>
              <span class="hero-subtext">{{ t('clientes atendidos') }}</span>
              <span class="hero-separator">·</span>
              <span class="hero-subvalue">{{ formatDecimal(cards.itens_por_cliente) }}</span>
              <span class="hero-subtext">{{ t('itens/cliente') }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Faixa 2: Secondary Cards -->
      <div class="kpi-grid kpi-grid--5 section-spacer">
        <StatsCard
          :value="formatDecimal(cards.meta_itens_dia)"
          :label="t('Meta de itens por dia (vendas)')"
          icon="pi pi-flag"
          variant="primary"
        />
        <StatsCard
          :value="formatDecimal(cards.media_diaria_realizada)"
          :label="t('Média diária realizada no mês (vendas)')"
          icon="pi pi-calendar"
          variant="info"
        />
        <StatsCard
          :value="cards.itens_em_atraso"
          :label="t('Itens em ATRASO para meta de venda')"
          icon="pi pi-exclamation-triangle"
          :variant="cards.itens_em_atraso > 0 ? 'danger' : 'success'"
        />
        <StatsCard
          :value="formatMoney(cards.ticket_medio_geral)"
          :label="t('Ticket médio geral')"
          icon="pi pi-ticket"
          variant="warning"
        />
        <router-link to="/producao-kpi" class="nav-card">
          <i class="pi pi-chart-bar"></i>
          <span>{{ t('Visualizar evolução de metas de produção') }}</span>
        </router-link>
      </div>

      <!-- Faixa 3: Charts Row -->
      <div class="charts-row section-spacer">
        <charts
          type="area"
          :series="itensVsVendasSeries"
          :options="itensVsVendasOptions"
          :is-loading="isLoading"
          height="350px"
          wrapper-class="chart-box"
        />
        <charts
          type="bar"
          :series="vendasDiariasSeries"
          :options="vendasDiariasOptions"
          :is-loading="isLoading"
          height="350px"
          wrapper-class="chart-box"
        />
      </div>

      <!-- Faixa 4: Fidelizados -->
      <div class="section-spacer">
        <charts
          type="bar"
          :series="fidelizadosSeries"
          :options="fidelizadosOptions"
          :is-loading="isLoading"
          height="350px"
          wrapper-class="chart-box"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDashboardVendasKpi } from '../../composables/dashboard/useDashboardVendasKpi';
import { useChartFormat } from '../../composables/useChartFormat';
import PageHeader from '../../ui/PageHeader.vue';
import DashboardSkeletonBlock from '../dashboard/widgets/DashboardSkeletonBlock.vue';
import StatsCard from '../../ui/StatsCard.vue';

const { t, n } = useI18n();
const { currencySymbol, monthLabel, dayLabel } = useChartFormat();
const {
  cards,
  evolucaoDiaria,
  fidelizados,
  isLoading,
  error,
  fetchAll,
} = useDashboardVendasKpi();

// Empty state: true when ALL resource-count cards values are zero.
function isEmptyData(): boolean {
  const c = cards.value;
  if (!c) return false;
  return (
    c.meta_faturamento === 0 &&
    c.meta_itens === 0 &&
    c.dias_uteis === 0 &&
    c.vendas_realizadas === 0 &&
    c.itens_realizados === 0 &&
    c.clientes_atendidos === 0 &&
    c.previsao_vendas === 0 &&
    c.itens_previstos === 0 &&
    c.ticket_medio_geral === 0 &&
    c.ticket_medio_item === 0 &&
    c.meta_itens_dia === 0 &&
    c.media_diaria_realizada === 0 &&
    c.itens_em_atraso === 0 &&
    c.percentual_atingimento === 0 &&
    c.itens_por_cliente === 0
  );
}

// ─── Helpers ───────────────────────────────────────────────────

function formatPercent(bp: number): string {
  return `${(bp / 100).toFixed(1)}%`;
}

function formatMoney(centavos: number): string {
  return n(centavos / 100, 'currency');
}

function formatDecimal(valor100: number): string {
  return n(valor100 / 100, 'decimal');
}

function percentClass(bp: number): string {
  if (bp >= 10000) return 'text-success';
  if (bp >= 5000) return 'text-warning';
  return 'text-danger';
}

// ─── Chart: Itens Recebidos vs Vendas Realizadas (Área) ───────

const itensVsVendasSeries = computed(() => {
  if (!evolucaoDiaria.value?.length) return [];
  return [
    {
      name: t('Itens recebidos'),
      data: evolucaoDiaria.value.map((p) => p.itens_recebidos ?? 0),
    },
    {
      name: t('Vendas realizadas'),
      data: evolucaoDiaria.value.map((p) => Math.round((p.vendas_realizadas ?? 0) / 100)),
    },
  ];
});

const itensVsVendasOptions = computed(() => ({
  chart: { stacked: false },
  xaxis: {
    categories: evolucaoDiaria.value?.map((p) => dayLabel(p.data)) ?? [],
  },
  yaxis: { title: { text: t('Quantidade') } },
  colors: ['var(--p-primary-500)', 'var(--p-green-500)'],
  stroke: { curve: 'smooth' as const, width: 2 },
  fill: {
    type: 'gradient',
    gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0.1 },
  },
  dataLabels: { enabled: false },
  tooltip: { shared: true, intersect: false },
}));

// ─── Chart: Vendas Realizadas Diárias (Barras) ────────────────

const vendasDiariasSeries = computed(() => {
  if (!evolucaoDiaria.value?.length) return [];
  return [
    {
      name: t('Vendas realizadas'),
      data: evolucaoDiaria.value.map((p) => Math.round((p.vendas_realizadas ?? 0) / 100)),
    },
  ];
});

const vendasDiariasOptions = computed(() => ({
  chart: { stacked: false },
  xaxis: {
    categories: evolucaoDiaria.value?.map((p) => dayLabel(p.data)) ?? [],
  },
  yaxis: { title: { text: t('Valor ({moeda})', { moeda: currencySymbol() }) } },
  colors: ['var(--p-green-500)'],
  dataLabels: { enabled: false },
}));

// ─── Chart: Clientes Fidelizados (Barras Agrupadas) ───────────

const fidelizadosSeries = computed(() => {
  if (!fidelizados.value?.length) return [];
  return [
    {
      name: t('Clientes atendidos'),
      data: fidelizados.value.map((m) => m.total_clientes),
    },
    {
      name: t('Clientes fidelizados'),
      data: fidelizados.value.map((m) => m.clientes_fidelizados),
    },
  ];
});

const fidelizadosOptions = computed(() => ({
  chart: { stacked: false },
  xaxis: {
    categories: fidelizados.value?.map((m) => monthLabel(m.mes)) ?? [],
  },
  yaxis: { title: { text: t('Clientes') } },
  colors: ['var(--p-primary-500)', 'var(--p-amber-500)'],
  dataLabels: { enabled: false },
  tooltip: { shared: true, intersect: false },
}));



</script>

<style scoped>
.vendas-kpi-page {
  padding: 20px;
}

/* Hero Cards */
.hero-card {
  background: var(--p-surface-card);
  border: 1px solid var(--p-surface-border);
  border-radius: 12px;
  padding: 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.hero-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.hero-indicator {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  transition: background 0.3s ease;
}

.variant-primary .hero-indicator {
  background: linear-gradient(90deg, var(--p-primary-400), var(--p-primary-600));
}
.variant-info .hero-indicator {
  background: linear-gradient(90deg, var(--p-primary-500), var(--p-primary-600));
}
.variant-success .hero-indicator {
  background: linear-gradient(90deg, var(--p-green-500), var(--p-green-600));
}

.hero-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.hero-value {
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--p-text-color);
}

.hero-label {
  font-size: 13px;
  color: var(--p-text-color-secondary);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.hero-subinfo {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  font-size: 14px;
}

.hero-percent {
  font-weight: 700;
  font-size: 18px;
}

.hero-subvalue {
  font-weight: 600;
  color: var(--p-text-color);
}

.hero-subtext {
  color: var(--p-text-color-secondary);
}

.hero-separator {
  color: var(--p-text-color-secondary);
}

.text-success { color: var(--p-green-600); }
.text-warning { color: var(--p-amber-600); }
.text-danger  { color: var(--p-red-600); }

/* Grid */
.kpi-grid {
  display: grid;
  gap: 1rem;
}
.kpi-grid--3 { grid-template-columns: repeat(3, 1fr); }
.kpi-grid--5 { grid-template-columns: repeat(5, 1fr); }

@media (max-width: 1100px) {
  .kpi-grid--3 { grid-template-columns: repeat(2, 1fr); }
  .kpi-grid--5 { grid-template-columns: repeat(3, 1fr); }
}
@media (max-width: 900px) {
  .kpi-grid--3 { grid-template-columns: 1fr; }
  .kpi-grid--5 { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 500px) {
  .kpi-grid--5 { grid-template-columns: 1fr; }
}

/* Charts Row */
.charts-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

@media (max-width: 1100px) {
  .charts-row { grid-template-columns: 1fr; }
}

.chart-box {
  background: var(--p-surface-card);
  border: 1px solid var(--p-surface-border);
  border-radius: 12px;
  padding: 1rem;
}

/* Empty & Error States */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: var(--p-text-color-secondary);
  text-align: center;
  gap: 1rem;
}

.empty-icon {
  font-size: 3rem;
  opacity: 0.5;
}

/* Nav Card */
.nav-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: var(--p-surface-card);
  border: 1px dashed var(--p-surface-border);
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  text-decoration: none;
  color: var(--p-primary-color);
  transition: all 0.3s ease;
  font-size: 13px;
  text-align: center;
  font-weight: 500;
}

.nav-card:hover {
  border-color: var(--p-primary-color);
  background: var(--p-primary-50);
  transform: translateY(-2px);
}

.nav-card i {
  font-size: 24px;
}

/* Spacer classes replacing inline margin-top */
.section-spacer { margin-top: 1rem; }
.loading-spacer { margin-top: 1rem; }
</style>
